<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

import BaseButton from "@/components/ui/BaseButton/BaseButton.vue";
import BaseTooltip from "@/components/ui/BaseTooltip/BaseTooltip.vue";
import DropdownMenu from "@/components/ui/DropdownMenu/DropdownMenu.vue";
import MenuList from "@/components/ui/MenuList/MenuList.vue";
import MenuListHeader from "@/components/ui/MenuList/MenuListHeader.vue";
import MenuListItem from "@/components/ui/MenuList/MenuListItem.vue";
import MenuListItems from "@/components/ui/MenuList/MenuListItems.vue";
import MenuListTitle from "@/components/ui/MenuList/MenuListTitle.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import type { Icon } from "@/model/icon";
import type { RAG } from "@/model/objective";

const { t } = useI18n();

interface RAGOption {
  id: RAG;
  icon: Icon;
  label: string;
}

const props = defineProps<{ rag: RAG }>();

const emit = defineEmits<(e: "update:rag", value: RAG) => void>();

const dropdownRef = useTemplateRef("dropdown");

const selected = ref(props.rag);

watch(
  () => props.rag,
  (val) => {
    selected.value = val;
  },
);

const colors: RAG[] = ["red", "amber", "green"];

const ragOptions: Record<RAG, RAGOption> = {
  red: { id: "red", icon: "rag/red", label: t("objectives.rag.red") },
  amber: { id: "amber", icon: "rag/amber", label: t("objectives.rag.amber") },
  green: { id: "green", icon: "rag/green", label: t("objectives.rag.green") },
};

const selectedOption = computed(() => ragOptions[selected.value]);

function updateSelection(id: RAG) {
  if (id in ragOptions) {
    selected.value = id;
    emit("update:rag", id);
  }
  dropdownRef.value?.close();
}
</script>

<template>
  <DropdownMenu
    ref="dropdown"
    class="rag-dropdown"
    width="sm"
    stay-open-on-content-click
    @click.stop
    @keydown.enter.stop
  >
    <template #trigger="{ isOpen }">
      <BaseTooltip position="bottom">
        <BaseButton
          class="rag-trigger"
          variant="ghost"
          color="grey"
          :activated="isOpen"
          :aria-label="selectedOption.label"
          :aria-expanded="isOpen"
          aria-haspopup="listbox"
          data-testid="rag-dropdown-trigger"
          data-dropdown-trigger
        >
          <div class="rag-container">
            <div class="rag-track" :class="`selected-${selected}`">
              <div
                v-for="color in colors"
                :key="color"
                class="circle"
                :class="[`rag-${color}`, { 'is-selected': color === selected }]"
              />
            </div>
          </div>
        </BaseButton>
        <template #content>
          {{ selectedOption.label }}
        </template>
      </BaseTooltip>
    </template>
    <MenuList role="dialog" aria-labelledby="rag-title" selectable>
      <MenuListHeader id="rag-title">
        <MenuListTitle>{{ t("objectives.rag") }}</MenuListTitle>
      </MenuListHeader>
      <MenuListItems>
        <MenuListItem
          v-for="(option, i) in ragOptions"
          :key="option.id"
          v-autofocus="i === selected"
          :selected="option.id === selected"
          @click="updateSelection(option.id)"
        >
          <template #before>
            <SvgIcon :name="option.icon" class="status-icon" />
          </template>
          {{ option.label }}
        </MenuListItem>
      </MenuListItems>
    </MenuList>
  </DropdownMenu>
</template>

<style lang="scss" scoped>
@use "@/styles/variables/colors";
@use "@/styles/board";

@mixin board-scale {
  --r-size: #{board.len(20px)};
  --circle-size: #{board.len(10px)};
  --gap: #{board.len(2px)};

  border-radius: board.len(6px);

  :deep(.base-tooltip) {
    display: block;
  }

  :deep(.base-button) {
    font-size: unset;
  }

  .rag-trigger {
    border-radius: board.len(6px);
  }
}

@mixin fixed-scale {
  --r-size: 28px;
  --circle-size: 14px;
  --gap: 2.5px;

  border-radius: 6px;
}

.rag-dropdown {
  background-color: colors.$background-grey;

  &.board-scale {
    @include board-scale;
  }

  &.fixed-scale {
    @include fixed-scale;
  }

  .rag-trigger {
    display: flex;
    height: var(--r-size);
    width: var(--r-size);
    padding: 0;

    .rag-container {
      display: flex;
      align-items: center;
      overflow: hidden;

      .rag-track {
        display: flex;
        gap: var(--gap);
        transition: transform 300ms ease;

        &.selected-red {
          transform: translateX(calc(var(--r-size) / 2 - var(--circle-size) / 2));
        }

        &.selected-amber {
          transform: translateX(
            calc(var(--r-size) / 2 - (var(--circle-size) + var(--gap) + var(--circle-size) / 2))
          );
        }

        &.selected-green {
          transform: translateX(
            calc(
              var(--r-size) / 2 - (2 * var(--circle-size) + 2 * var(--gap) + var(--circle-size) / 2)
            )
          );
        }

        .circle {
          width: var(--circle-size);
          height: var(--circle-size);
          border-radius: 50%;
          background-color: colors.$rag-grey;
          transition: background-color 300ms ease;

          &.rag-red.is-selected {
            background-color: colors.$rag-red;
          }

          &.rag-amber.is-selected {
            background-color: colors.$rag-amber;
          }

          &.rag-green.is-selected {
            background-color: colors.$rag-green;
          }
        }
      }
    }
  }
}
</style>
